import api from "../services/authService";

export const fetchNotificationSettings = async () => {
  const response = await api.get(`/settings/notification-settings/`);
  return response;
};

export const postNotificationSettings = async (prefs) => {
  const payload = {
    sms: {
      signature_request: prefs.signatureRequestSms,
      signature_reminder: prefs.signatureReminderSms,
    },
    email: {
      signature_request: prefs.signatureRequestEmail,
      signature_reminder: prefs.signatureReminderEmail,
      external_share: prefs.externalShareEmail,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const response = await api.post(`/settings/notification-settings/`, payload);
  return response;
};

export const addNewCategory = async (code, name) => {
  const payload = {
    code,
    name,
  };

  const response = await api.post(`/settings/categories/`, { ...payload });
  return response;
};

export const updateCategory = async (categoryId, data) => {
  const response = await api.patch(`/settings/categories/${categoryId}/`, data);
  return response;
};

export const deleteCategory = async (categoryId) => {
  const response = await api.delete(`/settings/categories/${categoryId}/`);
  return response;
};

export const fetchFileTemplates = async (categoryId) => {
  const url = categoryId
    ? `/settings/categories/?category_code=${categoryId}`
    : `/settings/categories/`;

  const response = await api.get(url);
  return response;
};

export const uploadFileTemplate = async (file, category) => {
  const url = `/settings/file-templates/`;

  const formData = new FormData();
  formData.append("file", file);

  if (category) {
    formData.append("category", category);
  }

  const response = await api.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response;
};

export const deleteTemplate = async (documentId) => {
  const response = await api.delete(`/formstack/templates/${documentId}/delete/`);
  return response;
};

export const updateTemplateStatus = async (templateId, isActive) => {
  const url = `/settings/file-templates/${templateId}/`;
  const data = { is_active: isActive };

  const response = await api.patch(url, data);
  return response;
};

export const updateTemplateOutputName = async (templateId, outputName) => {
  const response = await api.patch(`/settings/file-templates/${templateId}/`, {
    output_name: outputName,
  });
  return response;
};

export const updateTemplateCategory = async (templateId, categoryCode) => {
  const response = await api.patch(`/settings/file-templates/${templateId}/`, {
    category_code: categoryCode,
  });
  return response;
};

export const updateLetterheadTemplateStatus = async (templateId, isActive) => {
  const url = `/settings/letterhead-templates/${templateId}/`;
  const data = { is_active: isActive };

  const response = await api.patch(url, data);
  return response;
};

export const fetchLetterheadTemplatePreview = async (templateId) => {
  const url = `/settings/letterhead-templates/${templateId}/preview/`;
  const response = await api.get(url);
  return response;
};

export const fetchTemplatePreview = async (documentId, routeKey) => {
  const url = `/formstack/templates/${documentId}/preview/`;

  const params = {};
  if (routeKey) {
    params.route_key = routeKey;
  }

  const response = await api.get(url, { params });

  return response;
};

export const fetchTemplateCategories = async () => {
  const url = `formstack/templates/categories/`;

  const response = await api.get(url);
  return response;
};

export const fetchTemplatesByCategoryId = async (category_id) => {
  const url = `formstack/templates/?category=${category_id}`;

  const response = await api.get(url);
  return response;
};

export const addNewFirm = async (name) => {
  const payload = {
    name,
  };

  const response = await api.post(`/settings/firms/`, { ...payload });
  return response;
};

export const updateFirm = async (firmId, data) => {
  const response = await api.patch(`/settings/firms/${firmId}/`, data);
  return response;
};

export const deleteFirm = async (firmId) => {
  const response = await api.delete(`/settings/firms/${firmId}/`);
  return response;
};

export const fetchFirms = async () => {
  const response = await api.get(`/settings/firms/`);
  return response;
};

export const fetchLetterHeadTemplates = async (categoryId) => {
  const url = categoryId
    ? `/settings/letterhead-templates/?firm_id=${categoryId}`
    : `/settings/letterhead-templates/`;

  const response = await api.get(url);
  return response;
};

export const uploadLetterheadTemplate = async (formData) => {
  const url = `/settings/letterhead-templates/`;
  const response = await api.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

export const updateLetterheadTemplate = async (templateId, formData) => {
  const url = `/settings/letterhead-templates/${templateId}/`;
  const response = await api.patch(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

export const deleteLetterheadTemplate = async (templateId) => {
  const url = `/settings/letterhead-templates/${templateId}/`;
  const response = await api.delete(url);
  return response;
};

// ---- Template field mapping APIs ----

export const fetchTemplateMappings = async (formstackId) => {
  const response = await api.get(`/formstack/templates/${formstackId}/mappings/`);
  return response;
};

export const saveTemplateMappings = async (formstackId, mappings) => {
  const response = await api.put(`/formstack/templates/${formstackId}/mappings/`, {
    mappings,
  });
  return response;
};

// ---- Visual template editor ----

export const fetchTemplateLayout = async (formstackId, { refresh } = {}) => {
  const params = refresh ? "?refresh=1" : "";
  const response = await api.get(`/formstack/templates/${formstackId}/layout/${params}`);
  return response;
};

export const fetchTemplatePageImage = async (formstackId, pageNumber, scale = 1.5) => {
  const response = await api.get(
    `/formstack/templates/${formstackId}/pages/${pageNumber}/?scale=${encodeURIComponent(scale)}`,
    { responseType: "blob" }
  );
  return response;
};

// Word templates are edited in real Word (Word Online via SharePoint).
export const fetchTemplateWordDraft = async (formstackId) =>
  api.get(`/formstack/templates/${formstackId}/word-draft/`);

export const openTemplateWordDraft = async (formstackId) =>
  api.post(`/formstack/templates/${formstackId}/word-draft/`);

export const publishTemplateWordDraft = async (formstackId) =>
  api.post(`/formstack/templates/${formstackId}/word-draft/publish/`);

export const fetchTemplateSignatureFields = async (formstackId) => {
  const response = await api.get(`/formstack/templates/${formstackId}/signature-fields/`);
  return response;
};

export const saveTemplateSignatureFields = async (formstackId, signatureFields, { scope } = {}) => {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  const response = await api.put(`/formstack/templates/${formstackId}/signature-fields/${query}`, {
    signature_fields: signatureFields,
  });
  return response;
};

// ---- Power-of-attorney signature APIs ----

export const fetchAppointedPoa = async (matterId) => {
  const response = await api.get(`/matters/${matterId}/appointed-poa/`);
  return response;
};

export const fetchPoaSignatureAssets = async () => {
  const response = await api.get(`/formstack/poa-signature-assets/`);
  return response;
};

export const uploadPoaSignatureAsset = async (formData) => {
  const response = await api.post(`/formstack/poa-signature-assets/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

export const applyPoaSignature = async (documentId, attorneyName) => {
  const response = await api.post(
    `/documents/${documentId}/apply-poa-signature/`,
    { attorney_name: attorneyName }
  );
  return response;
};

// ---- Attorney signatures dragged onto a PDF ----

export const fetchAttorneySignatures = async () => {
  const response = await api.get(`/attorney-signatures/`);
  return response;
};

export const applyAttorneySignature = async (documentId, placements) => {
  const response = await api.post(
    `/documents/${documentId}/attorney-signature/`,
    { placements }
  );
  return response;
};

export const fetchSalesforceFields = async (objectType, { refresh } = {}) => {
  const params = refresh ? "?refresh=1" : "";
  const response = await api.get(`/salesforce/fields/${objectType}/${params}`);
  return response;
};

export const fetchTransformationChoices = async () => {
  const response = await api.get(`/templates/transformation-choices/`);
  return response;
};

export const fetchPopularFields = async () => {
  const response = await api.get(`/templates/popular-fields/`);
  return response;
};

export const testMappingPreview = async (formstackId, matterId) => {
  const response = await api.post(
    `/formstack/templates/${formstackId}/mapping-preview/`,
    { matter_id: matterId },
    { timeout: 180000 }
  );
  return response;
};

export const previewMappingDocument = async (formstackId, matterId, letterheadId, fieldOverrides = {}) => {
  const params = new URLSearchParams();
  if (letterheadId) params.append("letterhead_id", letterheadId);
  const queryString = params.toString();
  const url = queryString
    ? `/formstack/templates/${formstackId}/mapping-document-preview/?${queryString}`
    : `/formstack/templates/${formstackId}/mapping-document-preview/`;
  const response = await api.post(
    url,
    { matter_id: matterId, field_overrides: fieldOverrides || {} },
    { responseType: "blob", timeout: 180000 }
  );
  return response;
};

export const downloadTemplateWithMappings = async (formstackId) => {
  const response = await api.post(
    `/formstack/templates/${formstackId}/download-with-mappings/`,
    {},
    { responseType: "blob" }
  );
  return response;
};

export const searchMatters = async (query) => {
  const response = await api.get(
    `/matters/?search=${encodeURIComponent(query)}&page_size=10`
  );
  return response;
};

export const fetchMatterFolders = async (matterId) => {
  const response = await api.get(
    `/folders/?matter_id=${encodeURIComponent(matterId)}&include_documents=false`
  );
  return response;
};

// ---------------------------------------------------------------------------
// Generated Documents settings
// ---------------------------------------------------------------------------

export const fetchGeneratedDocumentFolders = async () => {
  const response = await api.get(`/settings/generated-document-folders/`);
  return response;
};

export const createGeneratedDocumentFolder = async (data) => {
  const response = await api.post(`/settings/generated-document-folders/`, data);
  return response;
};

export const updateGeneratedDocumentFolder = async (folderId, data) => {
  const response = await api.patch(`/settings/generated-document-folders/${folderId}/`, data);
  return response;
};

export const deleteGeneratedDocumentFolder = async (folderId) => {
  const response = await api.delete(`/settings/generated-document-folders/${folderId}/`);
  return response;
};

export const fetchGeneratedFolderDocuments = async (folderId) => {
  const response = await api.get(`/settings/generated-document-folders/${folderId}/documents/`);
  return response;
};

export const moveGeneratedDocument = async (documentId, folderTemplateId) => {
  const response = await api.post(`/settings/generated-documents/${documentId}/move/`, {
    folder_template_id: folderTemplateId,
  });
  return response;
};

// ---------------------------------------------------------------------------
// Template Packages
// ---------------------------------------------------------------------------

export const fetchPackages = async (categoryCode) => {
  const params = categoryCode ? `?category=${categoryCode}` : "";
  const response = await api.get(`/formstack/packages/${params}`);
  return response;
};

export const createPackage = async (data) => {
  const response = await api.post("/formstack/packages/", data);
  return response;
};

export const updatePackage = async (packageId, data) => {
  const response = await api.patch(`/formstack/packages/${packageId}/`, data);
  return response;
};

export const deletePackage = async (packageId) => {
  const response = await api.delete(`/formstack/packages/${packageId}/`);
  return response;
};

export const mergePackage = async (packageId, data) => {
  const response = await api.post(
    `/formstack/packages/${packageId}/merge/`,
    data
  );
  return response;
};

export const previewPackage = async (packageId, data) => {
  const response = await api.post(
    `/formstack/packages/${packageId}/preview/`,
    data,
    { timeout: 180000 }
  );
  return response;
};

export const combinePackageForSigning = async (packageId, data) => {
  const response = await api.post(
    `/formstack/packages/${packageId}/combine-for-signing/`,
    data
  );
  return response;
};

export const fetchGeneratedDocumentDownloadUrl = async (documentId) => {
  const response = await api.get(`/documents/presigned-url/${documentId}/`);
  return response;
};

export const renameGeneratedDocument = async (documentId, name) => {
  const response = await api.post(`/documents/${documentId}/rename/`, { name });
  return response;
};

export const deleteGeneratedDocument = async (documentId) => {
  const response = await api.delete(`/documents/${documentId}/delete/`);
  return response;
};

export const fetchRoutingRules = async () => {
  const response = await api.get(`/settings/routing-rules/`);
  return response;
};

export const createRoutingRule = async (data) => {
  const response = await api.post(`/settings/routing-rules/`, data);
  return response;
};

export const updateRoutingRule = async (ruleId, data) => {
  const response = await api.patch(`/settings/routing-rules/${ruleId}/`, data);
  return response;
};

export const deleteRoutingRule = async (ruleId) => {
  const response = await api.delete(`/settings/routing-rules/${ruleId}/`);
  return response;
};

export const fetchRoutingRuleOptions = async () => {
  const response = await api.get(`/settings/routing-rules/options/`);
  return response;
};

export const fetchTemplateFile = async (formstackId) => {
  const response = await api.get(
    `/formstack/templates/${formstackId}/file-content/`,
    { responseType: "blob" }
  );
  return response;
};

/** Delete fillable boxes from a PDF template; the trimmed file goes to Formstack. */
export const removeTemplateFields = async (formstackId, fieldNames) => {
  const response = await api.post(`/formstack/templates/${formstackId}/fields/remove/`, {
    field_names: fieldNames,
  });
  return response;
};

export const saveTemplateFile = async (formstackId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.put(
    `/formstack/templates/${formstackId}/file-content/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response;
};

// ---------------------------------------------------------------------------
// Package-level Formstack Sign overlay template mapping rules
// ---------------------------------------------------------------------------

export const fetchSignTemplateMappingRules = async (packageId) => {
  const response = await api.get(
    `/formstack/packages/${packageId}/sign-template-mapping-rules/`
  );
  return response;
};

export const saveSignTemplateMappingRules = async (packageId, payload) => {
  const response = await api.put(
    `/formstack/packages/${packageId}/sign-template-mapping-rules/`,
    payload
  );
  return response;
};